import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { FaEye, FaUser, FaTag, FaCheck } from "react-icons/fa";
import { FaSquareMinus } from "react-icons/fa6";
import { Box, Flex } from "@chakra-ui/react";
import { toTablePageableData, getEmptyPageResponse } from "utils/utils";
import {
    notificationsService,
    TRAFFIC_VIOLATION_DOCUMENT_CREATED_TYPE,
    PROCESS_CREATED_TYPE,
} from "services/notificationsService";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import NotificationsFilter from "./NotificationsFilter";
import ConfirmDialogWithFormModal from "./ConfirmDialogWithFormModal";
const { PERMISSIONS } = require("constants/permission");
import { AuthContext } from "contexts/AuthContext";
import { currentUserHasPermission } from "utils/permission";
import {
    ALERTS_DEFAULT_LIST_STATUS,
    ALERTS_LIST_URL_DEFAULTS,
    alertsFiltersShowActiveHint,
} from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";
import { DESC } from "constants/pagination";

function buildNotificationApiFilters(filters) {
    if (filters == null || typeof filters !== "object") {
        return { status: ALERTS_DEFAULT_LIST_STATUS };
    }
    const hasExtra = ["numero", "id_usuario_responsavel", "data_inicial", "data_final"].some(
        (k) => filters[k] != null && String(filters[k]).trim() !== ""
    );
    const st = filters.status;
    if (st === "") {
        return { ...filters };
    }
    if (st != null && String(st).trim() !== "") {
        return { ...filters };
    }
    if (hasExtra) {
        return { ...filters, status: ALERTS_DEFAULT_LIST_STATUS };
    }
    return { status: ALERTS_DEFAULT_LIST_STATUS };
}

const NotificationsList = () => {
    const { t } = useTranslation();
    const { searchParams, setSearchParams } = useListUrlSync(ALERTS_LIST_URL_DEFAULTS);
    const showFiltersHint = alertsFiltersShowActiveHint(searchParams.filters);
    const [response, setResponse] = useState(undefined);
    const [isLoadingResponse, setIsLoadingResponse] = useState(true);
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const resolveDialogRef = useRef(undefined);
    const [notificationToResolve, setNotificationToResolve] = useState(undefined);
    const reopenDialogRef = useRef(undefined);
    const [notificationToReopen, setNotificationToReopen] = useState(undefined);
    const insertIssueDialogRef = useRef(undefined);
    const [notificationToInsertIssue, setNotificationToInsertIssue] = useState(undefined);
    const removeIssueDialogRef = useRef(undefined);
    const [notificationToRemoveIssue, setNotificationToRemoveIssue] = useState(undefined);
    const assignNotificationDialogRef = useRef(undefined);
    const [notificationToAssign, setNotificationToAssign] = useState(undefined);
    const { getUserData } = useContext(AuthContext);
    const user = getUserData();

    const columns = useMemo(() => ([
    { Header: t("alerts.list.table.header.number"), accessor: "numero" },
    { Header: t("alerts.list.table.header.date"), accessor: "data_criacao" },
    {
        Header: t("alerts.list.table.header.type"),
        accessor: "tipo",
        custom: ({ value }) => customTipoLabel(value),
    },
    { Header: t("alerts.list.table.header.description"), accessor: "descricao" },
    { Header: t("alerts.list.table.header.date-solution"), accessor: "data_resolucao" },
    { Header: t("alerts.list.table.header.responsible"), accessor: "nome_usuario_responsavel" },
    {
        Header: t("system.labels.actions"),
        disableSortBy: true,
        actions: [
            {
                label: t("alerts.list.table.actions.details"),
                icon: <FaEye />,
                visible: (row) => {
                        return currentUserHasPermission([PERMISSIONS.PROCESS_CREATION, PERMISSIONS.VIOLATION_DOCUMENTS]);
                },
                onClick: ({ original }) => {handleClickAccessRegister(original.link)},
            },
            {
                label: t("alerts.list.table.actions.assign"),
                icon: <FaUser />,
                visible: () => true,
                onClick: ({ original }) => {handleClickAssignNotification(original.id)},
            },
            {
                label: t("alerts.list.table.actions.insert-issue"),
                icon: <FaTag />,
                visible: (row) => !row.data_pendencia && !row.data_resolucao,
                onClick: ({ original }) => {handleClickInsertIssue(original.id)},
            },
            {
                label: t("alerts.list.table.actions.remove-issue"),
                icon: <FaSquareMinus />,
                visible: (row) => row.data_pendencia,
                onClick: ({ original }) => {handleClickRemoveIssue(original.id)},
            },
            {
                label: t("alerts.list.table.actions.mark-as-resolved"),
                icon: <FaCheck />,
                visible: (row) => !row.data_resolucao,
                onClick: ({ original }) => {handleClickMarkAsResolved(original.id)},
            },
            {
                label: t("alerts.list.table.actions.reopen"),
                icon: <FaCheck />,
                visible: (row) => row.data_resolucao,
                onClick: ({ original }) => {handleClickReopen(original.id)},
            },
        ],
    }
    ]), []);

    const handleClickAccessRegister = (url) => {
        window.location.href = url;
    };

    const handleClickAssignNotification = (id) => {
        setNotificationToAssign(id);
        assignNotificationDialogRef.current?.displayDialog({
            title: t("alerts.list.table.actions.assign"),
            useResponsibleID: true
        });
    };

    const handleConfirmAssignNotification = async (responsibleUserID) => {
        if (notificationToAssign) {
            try {
                const notification = await notificationsService.find(notificationToAssign);
                const newNotification = {
                    ...notification,
                    id_usuario_responsavel: responsibleUserID
                };
                await notificationsService.assign(newNotification);
                showToast({ title: t('alerts.messages.success.assign'), status: "success" });
                void fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    const handleClickInsertIssue = (id) => {
        setNotificationToInsertIssue(id);
        insertIssueDialogRef.current?.displayDialog({
            title: t("alerts.list.table.actions.insert-issue"),
            useIssueDescription: true
        });
    };

    const handleConfirmInsertIssue = async (issueReason) => {
        if (notificationToInsertIssue) {
            try {
                const notification = await notificationsService.find(notificationToInsertIssue);
                const newNotification = {
                    ...notification,
                    motivo_pendencia: issueReason
                };
                await notificationsService.insertIssue(newNotification);
                showToast({ title: t('alerts.messages.success.insert-issue'), status: "success" });
                void fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    const handleClickRemoveIssue = (id) => {
        setNotificationToRemoveIssue(id);
        removeIssueDialogRef.current?.displayDialog({
            title: t("alerts.list.table.actions.remove-issue"),
            message: t("alerts.messages.actions.remove-issue-confirm"),
        });
    };

    const handleConfirmRemoveIssue = async () => {
        if (notificationToRemoveIssue) {
            try {
                await notificationsService.removeIssue(notificationToRemoveIssue);
                showToast({ title: t('alerts.messages.success.remove-issue'), status: "success" });
                void fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    const handleClickMarkAsResolved = (id) => {
        setNotificationToResolve(id);
        resolveDialogRef.current?.displayDialog({
            title: t("alerts.list.table.actions.mark-as-resolved"),
            message: t("alerts.messages.actions.mark-as-resolved-confirm"),
        });
    };

    const handleConfirmMarkAsResolved = async () => {
        if (notificationToResolve) {
            try {
                await notificationsService.resolve(notificationToResolve);
                showToast({ title: t('alerts.messages.success.mark-as-resolved'), status: "success" });
                void fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    const handleClickReopen = (id) => {
        setNotificationToReopen(id);
        reopenDialogRef.current?.displayDialog({
            title: t("alerts.list.table.actions.reopen"),
            message: (t("alerts.messages.actions.reopen-confirm")),
        });
    };

    const handleConfirmReopen = async () => {
        if (notificationToReopen) {
            try {
                await notificationsService.reopen(notificationToReopen);
                showToast({ title: t('alerts.messages.success.reopen'), status: "success" });
                void fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    const customTipoLabel = (value) => {
        if (value === PROCESS_CREATED_TYPE) {
            return t("alerts.list.table.types.process-created");
        }
        if (value === TRAFFIC_VIOLATION_DOCUMENT_CREATED_TYPE) {
            return t("alerts.list.table.types.violation-document-created")
        }
        return value;
    }

    const fetchData = async () => {
        setIsLoadingResponse(true);
        if (response) setResponse(undefined);

        try {
            const params = {
                ...searchParams,
                orderBy: searchParams.orderBy ?? "numero",
                orderDirection: searchParams.orderDirection ?? DESC,
                filters: buildNotificationApiFilters(searchParams.filters),
            };
            const data = await notificationsService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        } finally {
            setIsLoadingResponse(false);
        }
    };

    const updateSearchParams = (params = {}) => {
        const next = { ...params };
        if (next.orderBy === "number") {
            next.orderBy = "numero";
        }
        const page = Math.min(next.page ?? searchParams.page ?? 1, response?.total_pages ?? Infinity) || 1;
        setSearchParams({ ...searchParams, ...next, page });
    };

    const clearAlertsFilters = () => {
        setSearchParams((prev) => {
            const { filters: _drop, ...rest } = prev;
            return { ...rest, page: 1 };
        });
    };

    useEffect(() => {
        void fetchData();
    }, [searchParams]);

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Box alignSelf="flex-end">
                <NotificationsFilter
                    filterSnapshot={searchParams.filters}
                    onFilter={(filters) => updateSearchParams({ filters, page: 1 })}
                    onClearFilters={clearAlertsFilters}
                />
            </Box>
    
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("alerts.title")} subtitle={t("alerts.description")} />
        
                <CardBody>
                    <ListFiltersActiveAlert show={showFiltersHint} onClear={clearAlertsFilters} />
                    {isLoadingResponse ? (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    ) : (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                disableSearch={true}
                                tableData={response?.content ?? []}
                                pageableData={toTablePageableData(response, {
                                    defaultField: "numero",
                                    ...searchParams,
                                })}
                                onChange={(params) => {
                                    updateSearchParams(params);
                                }}
                            />
                        </Box>
                    )}
                </CardBody>
            </Card>
            <ConfirmDialog ref={reopenDialogRef} onConfirm={handleConfirmReopen} confirmButtonProps={{ colorScheme: "blue" }} />
            <ConfirmDialog ref={resolveDialogRef} onConfirm={handleConfirmMarkAsResolved} confirmButtonProps={{ colorScheme: "blue" }} />
            <ConfirmDialog ref={removeIssueDialogRef} onConfirm={handleConfirmRemoveIssue} confirmButtonProps={{ colorScheme: "blue" }} />
            <ConfirmDialogWithFormModal ref={insertIssueDialogRef} onConfirm={handleConfirmInsertIssue} confirmButtonProps={{ colorScheme: "blue" }} />
            <ConfirmDialogWithFormModal ref={assignNotificationDialogRef} onConfirm={handleConfirmAssignNotification} confirmButtonProps={{ colorScheme: "blue" }} />
        </Flex>
    );

};

export default NotificationsList;
